import { Injectable } from '@angular/core';
import { CultivationProductLinkApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/cultivation-product-link/cultivation-product-link-api';
import { WebViewBridgeService } from '../webview/webview-bridge.service';
import { WebViewAction } from '../../enums/webview-action';
import { PosthogService } from './posthog.service';

/**
 * Resolves the webshop URL for a cultivation (server-side, evolutive) and opens it in the
 * EXTERNAL browser. The resolution + URL format live in the backend so they can change
 * without an app release; the app just opens whatever URL the endpoint returns.
 */
@Injectable({ providedIn: 'root' })
export class ShopRedirectService {
  // SDK API is config-driven via the global SdkConfigService singleton, so a direct
  // instantiation works here (no Angular DI provider needed for a root service).
  private readonly cultivationProductLinkApi = new CultivationProductLinkApi();

  // Last-resort fallback ONLY if the resolve call itself is unreachable (network error):
  // the 99% path uses the backend-returned URL. Kept minimal so the button is never dead.
  private static readonly SHOP_HOME_FALLBACK = 'https://www.vegiwise.com';

  constructor(
    private webViewBridge: WebViewBridgeService,
    private posthogService: PosthogService,
  ) {}

  /**
   * Resolve the webshop URL for a cultivation and open it in the external browser.
   * Never throws.
   */
  async buyForCultivation(cultivationUid: string): Promise<void> {
    if (!cultivationUid) {
      return;
    }
    // Mobile browsers only allow window.open during the SYNCHRONOUS part of the
    // user gesture: pre-open the tab now (before any await), fill the URL later.
    // Inside the Flutter WebView the native channel is used instead (no tab).
    const pendingTab = this.hasNativeChannel() ? null : window.open('', '_blank');
    let url = ShopRedirectService.SHOP_HOME_FALLBACK;
    let source = 'fallback';
    try {
      const res = await this.cultivationProductLinkApi.resolveShopUrl(cultivationUid);
      // Only open an ABSOLUTE http(s) URL — a relative URL would open the app itself in the tab.
      if (res?.url && /^https?:\/\//i.test(res.url)) {
        url = res.url;
        source = res.source ?? 'fallback';
      }
    } catch (e) {
      console.error('Failed to resolve shop URL, opening shop homepage', e);
    }
    this.posthogService.capture('shop_redirect', { cultivationUid, source });
    this.openExternal(url, pendingTab);
  }

  private hasNativeChannel(): boolean {
    return (
      this.webViewBridge.isChannelAvailable(WebViewAction.openExternalUrl) ||
      this.webViewBridge.isChannelAvailable(WebViewAction.openOAuthUrl)
    );
  }

  /**
   * Open a URL in the EXTERNAL browser. Inside the Flutter WebView, *.vegiwise.com is
   * whitelisted, so a plain navigation would load the shop INSIDE the app — we MUST route
   * through the native channel. Cascade: dedicated channel → OAuth channel (older apps)
   * → pre-opened tab (popup-blocker safe) → window.open as a last resort.
   */
  private openExternal(url: string, pendingTab: Window | null = null): void {
    if (this.webViewBridge.isChannelAvailable(WebViewAction.openExternalUrl)) {
      this.webViewBridge.postMessageToChannel(WebViewAction.openExternalUrl, url);
    } else if (this.webViewBridge.isChannelAvailable(WebViewAction.openOAuthUrl)) {
      this.webViewBridge.postMessageToChannel(WebViewAction.openOAuthUrl, url);
    } else if (pendingTab && !pendingTab.closed) {
      pendingTab.location.href = url;
    } else {
      window.open(url, '_blank');
    }
  }
}
